// Greedy — Basic vs Optimized code for every "Beginner Problems" drill listed in the
// Greedy Explanation modal. Same shape as dp2dDrills.js: concise steps + an interactive
// "watch it happen" visualizer per version. One drill cross-references the curated problem.

const greedyDrills = [
  {
    id: 'best-time-to-buy-sell-stock-ii',
    title: 'Best Time to Buy and Sell Stock II',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Brute Force Recursion (Buy/Hold/Sell State Machine)',
        explanation: 'At each day, recursively decide whether to hold a share or not, trying every legal action (buy if not holding, sell if holding, or do nothing) and taking the best result. Correct, but "the best profit from day i onward, given a holding state" gets re-solved from scratch by every different earlier sequence of decisions that reaches the same (day, holding) pair.',
        steps: [
          'Base case: past the last day, no more profit is possible — return 0.',
          'Always consider doing nothing today and recursing on tomorrow.',
          'If not holding a share, consider buying today (pay the price) and recursing as "holding."',
          'If holding a share, consider selling today (gain the price) and recursing as "not holding." Take the best option found.',
        ],
        code:
`int maxProfitBasic(int[] prices, int day, boolean holding) {
    if (day == prices.length) return 0;
    int doNothing = maxProfitBasic(prices, day + 1, holding);
    int act = holding
        ? prices[day] + maxProfitBasic(prices, day + 1, false)
        : -prices[day] + maxProfitBasic(prices, day + 1, true);
    return Math.max(doNothing, act);
}`,
        complexity: { time: 'O(2ⁿ)', space: 'O(n) — recursion depth' },
      },
      {
        name: 'Optimized — Greedy: Sum Every Uphill Move',
        explanation: 'Since any number of transactions are allowed with no cooldown, the best strategy is simply to capture EVERY positive day-to-day price increase — buying the day before any uphill move and selling right after it is always at least as good as any more complex trading strategy.',
        steps: [
          'Walk through the prices once, comparing each day to the day before it.',
          'Whenever the price went up, add that day\'s gain directly to the running profit.',
          'Whenever the price went down or stayed flat, there\'s nothing to capture — skip it.',
          'The running total after one pass is the answer.',
        ],
        visual: {
          arrays: [{ label: 'prices', values: [7, 1, 5, 3, 6, 4] }],
          varNames: ['profit'],
          steps: [
            { highlights: { prices: [0, 1] }, vars: { profit: 0 }, note: 'i=1: prices[1]=1 < prices[0]=7 — no gain, skip.' },
            { highlights: { prices: [1, 2] }, vars: { profit: 4 }, note: 'i=2: prices[2]=5 > prices[1]=1 — gain of 4. profit=4.' },
            { highlights: { prices: [2, 3] }, vars: { profit: 4 }, note: 'i=3: prices[3]=3 < prices[2]=5 — no gain.' },
            { highlights: { prices: [3, 4] }, vars: { profit: 7 }, note: 'i=4: prices[4]=6 > prices[3]=3 — gain of 3. profit=7.' },
            { highlights: { prices: [4, 5] }, vars: { profit: 7 }, note: 'i=5: prices[5]=4 < prices[4]=6 — no gain. Final profit = 7, matching (5-1) + (6-3) = 4 + 3.' },
          ],
        },
        code:
`int maxProfit(int[] prices) {
    int profit = 0;
    for (int i = 1; i < prices.length; i++) {
        if (prices[i] > prices[i - 1]) {
            profit += prices[i] - prices[i - 1];
        }
    }
    return profit;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'assign-cookies',
    title: 'Assign Cookies',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Brute Force Recursion',
        explanation: 'For each child, recursively try either skipping them or assigning any single unused cookie large enough to satisfy them, taking whichever choice leads to more total satisfied children. Correct, but explores every combination of cookie-to-child assignments.',
        steps: [
          'Base case: past the last child, no more children can be satisfied.',
          'Always consider leaving the current child unsatisfied and moving to the next.',
          'Also try assigning each currently-unused cookie big enough for this child, recursing with it marked used.',
          'Take the maximum count of satisfied children found across every branch.',
        ],
        code:
`int findContentChildrenBasic(int[] g, int[] s, int childIdx, boolean[] used) {
    if (childIdx == g.length) return 0;
    int best = findContentChildrenBasic(g, s, childIdx + 1, used);
    for (int j = 0; j < s.length; j++) {
        if (!used[j] && s[j] >= g[childIdx]) {
            used[j] = true;
            best = Math.max(best, 1 + findContentChildrenBasic(g, s, childIdx + 1, used));
            used[j] = false;
        }
    }
    return best;
}`,
        complexity: { time: 'Exponential', space: 'O(children) — recursion depth' },
      },
      {
        name: 'Optimized — Greedy: Sort Both, Two Pointers',
        explanation: 'Sort both the children\'s greed factors and the cookie sizes. Then, always try to satisfy the LEAST greedy remaining child with the SMALLEST remaining cookie that still fits them — using a bigger cookie than necessary on an easy-to-please child only wastes a cookie that a greedier child might have needed.',
        steps: [
          'Sort both arrays ascending.',
          'Walk both with two pointers: if the current cookie satisfies the current child, that child is content — advance the child pointer.',
          'Always advance the cookie pointer, whether or not it was used — an unused cookie is now known to be too small for even the least greedy remaining child, or already spent.',
          'Return how many children were satisfied.',
        ],
        visual: {
          arrays: [
            { label: 'g (sorted greed factors)', values: [1, 2] },
            { label: 's (sorted cookie sizes)', values: [1, 2, 3] },
          ],
          varNames: [],
          steps: [
            { highlights: { 'g (sorted greed factors)': [0], 's (sorted cookie sizes)': [0] }, note: 's[0]=1 ≥ g[0]=1 — this cookie satisfies the child. Both pointers advance.' },
            { highlights: { 'g (sorted greed factors)': [1], 's (sorted cookie sizes)': [1] }, note: 's[1]=2 ≥ g[1]=2 — satisfies the next child too. Both pointers advance again.' },
            { note: 'Only 2 children total, both satisfied — cookie s[2]=3 is never even needed. Final answer: 2.' },
          ],
        },
        code:
`int findContentChildren(int[] g, int[] s) {
    Arrays.sort(g);
    Arrays.sort(s);
    int child = 0, cookie = 0;
    while (child < g.length && cookie < s.length) {
        if (s[cookie] >= g[child]) {
            child++;
        }
        cookie++;
    }
    return child;
}`,
        complexity: { time: 'O(n log n + m log m)', space: 'O(1) extra' },
      },
    ],
  },
  {
    id: 'jump-game-drill',
    title: 'Jump Game',
    level: 'Easy',
    crossReference:
      "This drill's full walkthrough — including a middle DP approach and the flagship greedy solution — lives " +
      "in this pattern's curated problem list — open \"Jump Game\" there for the complete step-by-step and all " +
      "three code versions. You can also try it hands-on in the \"Jump Game\" mode of the Interactive Practice playground.",
  },
  {
    id: 'jump-game-ii',
    title: 'Jump Game II (Minimum Jumps)',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Brute Force Recursion',
        explanation: 'From each position, recursively try every possible jump distance up to the maximum allowed, and take 1 + the fewest jumps needed from wherever that lands. Correct, but "the minimum jumps needed from position i" is asked repeatedly through many different earlier jump sequences that reach the same position.',
        steps: [
          'Base case: already at or past the last index needs 0 more jumps.',
          'Try every jump distance from 1 up to the current position\'s max reach.',
          'Recursively find the minimum jumps needed from each resulting position.',
          'Return 1 + the smallest of those results.',
        ],
        code:
`int jumpBasic(int[] nums, int pos) {
    if (pos >= nums.length - 1) return 0;
    int minJumps = Integer.MAX_VALUE;
    for (int step = 1; step <= nums[pos]; step++) {
        if (pos + step < nums.length) {
            int jumps = jumpBasic(nums, pos + step);
            if (jumps != Integer.MAX_VALUE) minJumps = Math.min(minJumps, 1 + jumps);
        }
    }
    return minJumps;
}`,
        complexity: { time: 'Exponential', space: 'O(n) — recursion depth' },
      },
      {
        name: 'Optimized — Greedy: Expand One "Level" at a Time',
        explanation: 'Think of each jump as reaching an entire RANGE of positions, like a level in a breadth-first search. Track the farthest reachable index seen so far; the moment the loop reaches the boundary of the CURRENT jump\'s range, a new jump is forced, and that new jump\'s range becomes the farthest reach discovered during the previous range.',
        steps: [
          'Track farthest (the best reach found anywhere so far) and currentEnd (the boundary of the jump currently "in use").',
          'At each index, update farthest using that index\'s own jump length.',
          'The moment the loop index reaches currentEnd, a jump must be taken: increment the jump count, and extend currentEnd to farthest.',
          'Stop one index early — no jump is needed once already standing on the last index.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [2, 3, 1, 1, 4] }],
          varNames: ['farthest', 'currentEnd', 'jumps'],
          steps: [
            { highlights: { nums: [0] }, vars: { farthest: 2, currentEnd: 2, jumps: 1 }, note: 'i=0: farthest=max(0,0+2)=2. i==currentEnd(0) → this jump\'s range is exhausted: jumps=1, currentEnd becomes 2.' },
            { highlights: { nums: [1] }, vars: { farthest: 4, currentEnd: 2, jumps: 1 }, note: 'i=1: farthest=max(2,1+3)=4. i(1) still within the current range (currentEnd=2).' },
            { highlights: { nums: [2] }, vars: { farthest: 4, currentEnd: 4, jumps: 2 }, note: 'i=2: farthest=max(4,2+1)=4. i==currentEnd(2) → jumps=2, currentEnd becomes 4.' },
            { highlights: { nums: [3] }, vars: { farthest: 4, currentEnd: 4, jumps: 2 }, note: 'i=3: farthest=max(4,3+1)=4. Still within range. Loop ends at i=4 (last index — no jump needed from there). Final answer: 2 jumps.' },
          ],
        },
        code:
`int jump(int[] nums) {
    int jumps = 0, currentEnd = 0, farthest = 0;
    for (int i = 0; i < nums.length - 1; i++) {
        farthest = Math.max(farthest, i + nums[i]);
        if (i == currentEnd) {
            jumps++;
            currentEnd = farthest;
        }
    }
    return jumps;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'gas-station-drill',
    title: 'Gas Station',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Try Every Starting Index',
        explanation: 'For each possible starting station, simulate the full circular trip and check whether the tank ever goes negative. Correct, but every starting index gets its own complete O(n) simulation, even though most of that work overlaps with simulations from other starting points.',
        steps: [
          'For each candidate starting station, reset the tank to 0.',
          'Simulate visiting every station in circular order, adding gas and subtracting cost.',
          'If the tank ever goes negative, this starting point fails — try the next one.',
          'The first starting point that completes the full circuit without going negative is the answer.',
        ],
        code:
`int canCompleteCircuitBasic(int[] gas, int[] cost) {
    int n = gas.length;
    for (int start = 0; start < n; start++) {
        int tank = 0;
        boolean success = true;
        for (int step = 0; step < n; step++) {
            int i = (start + step) % n;
            tank += gas[i] - cost[i];
            if (tank < 0) { success = false; break; }
        }
        if (success) return start;
    }
    return -1;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Greedy: One Pass, Reset on Deficit',
        explanation: 'The key insight: if the tank goes negative at some station j while starting from station i, then NONE of the stations between i and j (inclusive) can be a valid starting point either — starting later within that range only means arriving at each subsequent station with even less fuel. So the moment the tank goes negative, jump the candidate start straight past the failure point.',
        steps: [
          'Track a running total (over the whole trip) and a running tank (since the last reset).',
          'At each station, add its gas-minus-cost difference to both.',
          'The moment the tank goes negative, every station from the current candidate start through this one is provably invalid — reset the candidate start to the next station, and reset the tank to 0.',
          'If the total across the whole trip is non-negative, a valid circuit exists, and the surviving candidate start is it; otherwise, no solution exists.',
        ],
        visual: {
          arrays: [
            { label: 'gas', values: [1, 2, 3, 4, 5] },
            { label: 'cost', values: [3, 4, 5, 1, 2] },
          ],
          varNames: ['tank', 'total', 'start'],
          steps: [
            { highlights: { gas: [0], cost: [0] }, vars: { tank: 0, total: -2, start: 1 }, note: 'i=0: diff=1-3=-2. tank goes negative — reset: start=1, tank=0.' },
            { highlights: { gas: [1], cost: [1] }, vars: { tank: 0, total: -4, start: 2 }, note: 'i=1: diff=2-4=-2. tank negative again — reset: start=2, tank=0.' },
            { highlights: { gas: [2], cost: [2] }, vars: { tank: 0, total: -6, start: 3 }, note: 'i=2: diff=3-5=-2. tank negative again — reset: start=3, tank=0.' },
            { highlights: { gas: [3], cost: [3] }, vars: { tank: 3, total: -3, start: 3 }, note: 'i=3: diff=4-1=3. tank=3 — stays non-negative. start=3 survives.' },
            { highlights: { gas: [4], cost: [4] }, vars: { tank: 6, total: 0, start: 3 }, note: 'i=4: diff=5-2=3. tank=6. Loop ends: total=0 ≥ 0 — a valid circuit exists, starting at index 3.' },
          ],
        },
        code:
`int canCompleteCircuit(int[] gas, int[] cost) {
    int total = 0, tank = 0, start = 0;
    for (int i = 0; i < gas.length; i++) {
        int diff = gas[i] - cost[i];
        total += diff;
        tank += diff;
        if (tank < 0) {
            start = i + 1;
            tank = 0;
        }
    }
    return total >= 0 ? start : -1;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'partition-labels-drill',
    title: 'Partition Labels',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Grow Each Partition by Repeated Rescanning',
        explanation: 'For each partition, repeatedly scan the characters inside it, checking whether any of them reappears further to the right — growing the partition boundary each time one does, and rescanning from the top. Correct, but each rescan is O(partition size), and a partition can be rescanned many times as it grows.',
        steps: [
          'Start a partition at the current position.',
          'Scan every character currently inside the partition, finding the farthest position any of them repeats.',
          'If that farthest repeat extends past the current partition boundary, grow the boundary and rescan from scratch.',
          'Once a full scan finds no further growth needed, the partition is complete — record its length and start the next one.',
        ],
        code:
`List<Integer> partitionLabelsBasic(String s) {
    List<Integer> result = new ArrayList<>();
    int start = 0;
    while (start < s.length()) {
        int end = start;
        while (true) {
            int farthestRepeat = end;
            for (int k = start; k <= end; k++) {
                int last = s.lastIndexOf(s.charAt(k));   // O(n) scan every time
                farthestRepeat = Math.max(farthestRepeat, last);
            }
            if (farthestRepeat == end) break;
            end = farthestRepeat;
        }
        result.add(end - start + 1);
        start = end + 1;
    }
    return result;
}`,
        complexity: { time: 'O(n²) or worse — lastIndexOf rescans the whole string repeatedly', space: 'O(1) extra' },
      },
      {
        name: 'Optimized — Greedy: Precompute Last Occurrences Once',
        explanation: 'Precompute every character\'s LAST occurrence index across the whole string, just once, up front. Then a single pass tracks the current partition\'s required end as the maximum last-occurrence seen among characters visited so far — the moment the loop index reaches that boundary, the partition is provably complete.',
        steps: [
          'Build a lookup: for every character, its last occurrence index in the string.',
          'Walk the string once, extending "end" to the current character\'s last occurrence whenever it\'s farther out.',
          'The moment the loop index reaches "end," every character seen in this stretch is confirmed to never reappear later — cut a partition here.',
          'Start the next partition right after the cut.',
        ],
        visual: {
          arrays: [{ label: 's', values: ['c', 'a', 'e', 'd', 'b', 'd', 'e', 'd', 'd', 'a'] }],
          varNames: ['end'],
          steps: [
            { highlights: { s: [0] }, vars: { end: 0 }, note: `i=0 ('c'): last occurrence of 'c' is index 0. end=max(0,0)=0. i==end → cut! Partition length 1 (indices 0..0).` },
            { highlights: { s: [1] }, vars: { end: 9 }, note: `i=1 ('a'): last occurrence of 'a' is index 9. end=max(0,9)=9. i(1) != end(9) — keep growing.` },
            { highlights: { s: [3] }, vars: { end: 9 }, note: `i=3 ('d'): last occurrence of 'd' is index 8. end stays max(9,8)=9.` },
            { highlights: { s: [9] }, vars: { end: 9 }, note: `i=9 ('a'): last occurrence of 'a' is index 9. end stays 9. i==end → cut! Partition length 9 (indices 1..9). Final answer: [1, 9].` },
          ],
        },
        code:
`List<Integer> partitionLabels(String s) {
    int[] last = new int[26];
    for (int i = 0; i < s.length(); i++) {
        last[s.charAt(i) - 'a'] = i;
    }
    List<Integer> result = new ArrayList<>();
    int start = 0, end = 0;
    for (int i = 0; i < s.length(); i++) {
        end = Math.max(end, last[s.charAt(i) - 'a']);
        if (i == end) {
            result.add(end - start + 1);
            start = i + 1;
        }
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(1) — a fixed 26-entry lookup' },
      },
    ],
  },
  {
    id: 'task-scheduler',
    title: 'Task Scheduler',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Minute-by-Minute Simulation',
        explanation: 'Use a max-heap of remaining task counts, and at every single time unit, run whichever available task currently has the highest remaining count, putting it on a cooldown queue. Correct, and genuinely a form of greedy — but it re-derives the schedule minute by minute instead of computing the total length directly.',
        steps: [
          'Count each task\'s frequency, and load all nonzero counts into a max-heap.',
          'At every time unit, pop the most frequent available task, run it, and (if it still has remaining count) place it on a cooldown queue.',
          'Whenever a task\'s cooldown expires, return it to the max-heap as available again.',
          'Continue until both the heap and the cooldown queue are empty — the total elapsed time is the answer.',
        ],
        code:
`int leastIntervalBasic(char[] tasks, int n) {
    int[] freq = new int[26];
    for (char t : tasks) freq[t - 'A']++;
    PriorityQueue<Integer> maxHeap = new PriorityQueue<>(Collections.reverseOrder());
    for (int f : freq) if (f > 0) maxHeap.add(f);

    int time = 0;
    Queue<int[]> cooldown = new LinkedList<>();   // [count, readyAtTime]
    while (!maxHeap.isEmpty() || !cooldown.isEmpty()) {
        time++;
        if (!maxHeap.isEmpty()) {
            int count = maxHeap.poll() - 1;
            if (count > 0) cooldown.add(new int[]{count, time + n});
        }
        if (!cooldown.isEmpty() && cooldown.peek()[1] == time) {
            maxHeap.add(cooldown.poll()[0]);
        }
    }
    return time;
}`,
        complexity: { time: 'O(total time · log 26)', space: 'O(26)' },
      },
      {
        name: 'Optimized — Greedy Formula (No Simulation)',
        explanation: 'The most frequent task defines a rigid skeleton: it must run maxFreq times, each separated by at least n cooldown slots, creating (maxFreq-1) full blocks of size (n+1), plus a final run. Every OTHER task (including ties for the max) slots into those forced gaps for free, unless there are simply more tasks than gaps — in which case no idle time is needed at all.',
        steps: [
          'Count each task\'s frequency.',
          'Find maxFreq (the highest single frequency) and maxCount (how many tasks tie for that frequency).',
          'Compute intervalsNeeded = (maxFreq - 1) × (n + 1) + maxCount — the length of the rigid skeleton built around the most frequent task(s).',
          'The answer is the larger of intervalsNeeded and the total task count — if there are enough OTHER tasks to fill every gap, no idle time is needed at all.',
        ],
        visual: {
          arrays: [],
          varNames: ['maxFreq', 'maxCount', 'formula'],
          steps: [
            { vars: { maxFreq: 3, maxCount: 1 }, note: `Scan frequencies for tasks=[A,A,A,B,B,B], n=2: A has count 3 — the highest seen so far. maxFreq=3, maxCount=1.` },
            { vars: { maxFreq: 3, maxCount: 2 }, note: `B also has count 3 — ties the max. maxCount becomes 2.` },
            { vars: { maxFreq: 3, maxCount: 2, formula: 8 }, note: `intervalsNeeded = (maxFreq-1)×(n+1) + maxCount = (3-1)×(2+1) + 2 = 6 + 2 = 8. Compare against tasks.length=6: max(6, 8) = 8 — final answer.` },
          ],
        },
        code:
`int leastInterval(char[] tasks, int n) {
    int[] freq = new int[26];
    for (char t : tasks) freq[t - 'A']++;

    int maxFreq = 0, maxCount = 0;
    for (int f : freq) {
        if (f > maxFreq) { maxFreq = f; maxCount = 1; }
        else if (f == maxFreq) maxCount++;
    }

    int intervalsNeeded = (maxFreq - 1) * (n + 1) + maxCount;
    return Math.max(tasks.length, intervalsNeeded);
}`,
        complexity: { time: 'O(tasks.length)', space: 'O(1) — a fixed 26-entry lookup' },
      },
    ],
  },
  {
    id: 'candy',
    title: 'Candy',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Repeated Relaxation Until Stable',
        explanation: 'Start every child with 1 candy, then repeatedly scan the whole array looking for any rating-order violation (a child with a higher rating than a neighbor, but not enough candy relative to them), fixing it and looping again. Correct, but a single fix can require multiple full passes to fully propagate.',
        steps: [
          'Give every child 1 candy to start.',
          'Scan left to right and right to left across the whole array, checking every adjacent pair against the rating rule.',
          'Whenever a violation is found, bump the lower candy count up, and mark that a change happened.',
          'Repeat the full scan until an entire pass makes no changes — only then is the array guaranteed valid.',
        ],
        code:
`int candyBasic(int[] ratings) {
    int n = ratings.length;
    int[] candies = new int[n];
    Arrays.fill(candies, 1);
    boolean changed = true;
    while (changed) {
        changed = false;
        for (int i = 0; i < n; i++) {
            if (i > 0 && ratings[i] > ratings[i - 1] && candies[i] <= candies[i - 1]) {
                candies[i] = candies[i - 1] + 1;
                changed = true;
            }
            if (i < n - 1 && ratings[i] > ratings[i + 1] && candies[i] <= candies[i + 1]) {
                candies[i] = candies[i + 1] + 1;
                changed = true;
            }
        }
    }
    int total = 0;
    for (int c : candies) total += c;
    return total;
}`,
        complexity: { time: 'O(n²) worst case — repeated full passes until convergence', space: 'O(n)' },
      },
      {
        name: 'Optimized — Greedy: Two Fixed Passes',
        explanation: 'Split the single rule ("a higher-rated child gets more candy than EITHER neighbor") into two independent, one-directional passes: left-to-right only checks the LEFT neighbor, right-to-left only checks the RIGHT neighbor. Each pass alone is trivial to get right, and running both guarantees the full two-sided rule holds everywhere.',
        steps: [
          'Give every child 1 candy to start.',
          'Left-to-right pass: whenever a child\'s rating beats the child just before them, give them one more candy than that neighbor.',
          'Right-to-left pass: whenever a child\'s rating beats the child just after them, give them the LARGER of their current candy count or one more than that neighbor.',
          'Sum the final candy counts — no further passes are ever needed.',
        ],
        visual: {
          arrays: [
            { label: 'ratings', values: [1, 0, 2] },
            { label: 'candies', values: [1, 1, 1] },
          ],
          varNames: [],
          steps: [
            { note: 'Every child starts with 1 candy: candies=[1,1,1].' },
            { snapshots: { candies: [1, 1, 2] }, highlights: { candies: [2] }, note: 'Left-to-right: ratings[2]=2 > ratings[1]=0 → candies[2] = candies[1] + 1 = 2.' },
            { snapshots: { candies: [2, 1, 2] }, highlights: { candies: [0] }, note: 'Right-to-left: ratings[0]=1 > ratings[1]=0 → candies[0] = max(candies[0], candies[1]+1) = max(1, 2) = 2. Final candies=[2,1,2], total = 5.' },
          ],
        },
        code:
`int candy(int[] ratings) {
    int n = ratings.length;
    int[] candies = new int[n];
    Arrays.fill(candies, 1);
    for (int i = 1; i < n; i++) {
        if (ratings[i] > ratings[i - 1]) {
            candies[i] = candies[i - 1] + 1;
        }
    }
    for (int i = n - 2; i >= 0; i--) {
        if (ratings[i] > ratings[i + 1]) {
            candies[i] = Math.max(candies[i], candies[i + 1] + 1);
        }
    }
    int total = 0;
    for (int c : candies) total += c;
    return total;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'remove-duplicate-letters',
    title: 'Remove Duplicate Letters',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Try Every Ordering of the Distinct Letters',
        explanation: 'Collect the distinct letters, then try every possible ordering of them, keeping the lexicographically smallest ordering that can still be read off the original string as a valid subsequence. Correct, but the number of orderings grows factorially with the number of distinct letters.',
        steps: [
          'Collect the set of distinct letters appearing in the string.',
          'Generate orderings of those letters, smallest-first (lexicographic order).',
          'For each ordering, check whether it can be read off the original string in order as a genuine subsequence.',
          'Return the first (smallest) ordering that passes the check.',
        ],
        code:
`// Illustrative sketch — generates orderings of the distinct letters and returns
// the lexicographically smallest one that's a valid subsequence of s.
// The subsequence check and ordering generation are omitted for brevity;
// their combined cost grows factorially with the number of distinct letters.
String removeDuplicateLettersBasic(String s) {
    Set<Character> distinctLetters = new TreeSet<>();
    for (char c : s.toCharArray()) distinctLetters.add(c);
    return findSmallestValidOrdering(s, new ArrayList<>(distinctLetters));
}`,
        complexity: { time: 'O(k!) where k = number of distinct letters', space: 'O(k)' },
      },
      {
        name: 'Optimized — Greedy Stack: Pop Only If Safe',
        explanation: 'Build the answer as a stack, one character at a time. A character already in the stack is skipped entirely. Otherwise, pop the stack\'s top WHILE it\'s lexicographically larger than the current character AND it reoccurs later in the string (so popping it now doesn\'t lose it forever) — then push the current character.',
        steps: [
          'Precompute each character\'s LAST occurrence index in the string, once.',
          'Walk the string once. If the current character is already in the stack, skip it entirely.',
          'Otherwise, pop the stack while its top is larger than the current character AND that top character still occurs again later (checked via the precomputed last-occurrence index).',
          'Push the current character. The final stack, read bottom to top, is the answer.',
        ],
        visual: {
          arrays: [
            { label: 's = "cbacdcbc"', values: ['c', 'b', 'a', 'c', 'd', 'c', 'b', 'c'] },
            { label: 'stack (bottom→top)', values: ['c'] },
          ],
          varNames: [],
          steps: [
            { note: `i=0 ('c'): stack empty — push. stack=[c].` },
            { snapshots: { 'stack (bottom→top)': ['b'] }, highlights: { 's = "cbacdcbc"': [1] }, note: `i=1 ('b'): top='c' > 'b', and 'c' occurs again later (index 7) — safe to pop. Stack empties, then push 'b'. stack=[b].` },
            { snapshots: { 'stack (bottom→top)': ['a'] }, highlights: { 's = "cbacdcbc"': [2] }, note: `i=2 ('a'): top='b' > 'a', and 'b' occurs again later (index 6) — pop it, then push 'a'. stack=[a].` },
            { snapshots: { 'stack (bottom→top)': ['a', 'c'] }, highlights: { 's = "cbacdcbc"': [3] }, note: `i=3 ('c'): top='a' < 'c' — no pop. Push. stack=[a,c].` },
            { snapshots: { 'stack (bottom→top)': ['a', 'c', 'd'] }, highlights: { 's = "cbacdcbc"': [4] }, note: `i=4 ('d'): top='c' < 'd' — no pop. Push. stack=[a,c,d].` },
            { highlights: { 's = "cbacdcbc"': [5] }, note: `i=5 ('c'): already in the stack — skip entirely (no push, no pop).` },
            { snapshots: { 'stack (bottom→top)': ['a', 'c', 'd', 'b'] }, highlights: { 's = "cbacdcbc"': [6] }, note: `i=6 ('b'): top='d' > 'b', but 'd' does NOT occur again later (its last index is 4, already passed) — popping it would lose it forever, so it must stay. Push 'b'. stack=[a,c,d,b].` },
            { highlights: { 's = "cbacdcbc"': [7] }, note: `i=7 ('c'): already in the stack — skip. Final stack (bottom→top): [a,c,d,b] → "acdb".` },
          ],
        },
        code:
`String removeDuplicateLetters(String s) {
    int[] lastIndex = new int[26];
    for (int i = 0; i < s.length(); i++) lastIndex[s.charAt(i) - 'a'] = i;

    boolean[] inStack = new boolean[26];
    Deque<Character> stack = new ArrayDeque<>();
    for (int i = 0; i < s.length(); i++) {
        char c = s.charAt(i);
        if (inStack[c - 'a']) continue;
        while (!stack.isEmpty() && stack.peek() > c && lastIndex[stack.peek() - 'a'] > i) {
            inStack[stack.pop() - 'a'] = false;
        }
        stack.push(c);
        inStack[c - 'a'] = true;
    }
    StringBuilder sb = new StringBuilder();
    for (char c : stack) sb.append(c);
    return sb.reverse().toString();
}`,
        complexity: { time: 'O(n)', space: 'O(26) — bounded stack and lookup tables' },
      },
    ],
  },
];

export default greedyDrills;
